import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, Check, Trash2, CheckCheck, AlertTriangle, CalendarClock, CalendarDays } from 'lucide-react';
import ReminderCalendar from '../components/reminders/ReminderCalendar';
import { kpiCounts } from '../components/reminders/reminderModel';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Notifications = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Phase 3 (10/07/2026): pending reminders feed the Overdue / Today / Upcoming tiles and the Reminders tab.
  const [reminders, setReminders] = useState([]);
  const isAdmin = !!(user?.roles || []).includes('admin');

  const fetchReminders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reminders`, { headers: getAuthHeaders(), params: { status: 'pending', mine: true } });
      setReminders(res.data?.reminders || []);
    } catch (error) {
      setReminders([]);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);
  const onReminderChange = useCallback(() => { fetchReminders(); }, [fetchReminders]);
  const kpis = useMemo(() => kpiCounts(reminders, notifications), [reminders, notifications]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: getAuthHeaders(),
        params: {
          unread_only: filter === 'unread',
          limit: 100
        }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        null,
        { headers: getAuthHeaders() }
      );
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, null, { headers: getAuthHeaders() });
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { headers: getAuthHeaders() });
      toast.success('Notification deleted');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all read notifications?')) return;
    
    try {
      await axios.delete(`${API}/notifications/clear-all`, { headers: getAuthHeaders() });
      toast.success('Cleared all read notifications');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      deal_status_change: '📊',
      payment_due: '💰',
      application_status: '📝',
      commission_approved: '✅',
      new_deal: '🎉',
      reminder_due: '⏰',
      default: '🔔'
    };
    return icons[type] || icons.default;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'border-l-4 border-red-500 bg-red-50',
      high: 'border-l-4 border-orange-500 bg-orange-50',
      normal: 'border-l-4 border-blue-500',
      low: 'border-l-4 border-gray-500'
    };
    return colors[priority] || colors.normal;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
            <p className="text-gray-600">Stay updated with important alerts and changes</p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={markAllAsRead}>
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
            <Button variant="outline" onClick={clearAll}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Read
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="notification-kpis">
          {[
            { key: 'unread', label: 'Unread', value: kpis.unread, icon: Bell, tone: 'text-blue-600', go: 'unread' },
            { key: 'overdue', label: 'Overdue reminders', value: kpis.overdue, icon: AlertTriangle, tone: kpis.overdue ? 'text-red-600' : 'text-slate-400', go: 'reminders' },
            { key: 'today', label: 'Due today', value: kpis.today, icon: CalendarClock, tone: kpis.today ? 'text-amber-600' : 'text-slate-400', go: 'reminders' },
            { key: 'upcoming', label: 'Upcoming', value: kpis.upcoming, icon: CalendarDays, tone: 'text-slate-600', go: 'reminders' },
          ].map(({ key, label, value, icon: Icon, tone, go }) => (
            <button type="button" key={key} onClick={() => setFilter(go)}
              className={`text-left rounded-lg border bg-white p-4 hover:shadow-sm transition-shadow ${filter === go && go !== 'unread' ? 'border-slate-400' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
                <Icon className={`h-4 w-4 ${tone}`} />
              </div>
              <div className={`text-2xl font-semibold tabular-nums mt-1 ${tone}`}>{value}</div>
            </button>
          ))}
        </div>

        <Tabs value={filter} onValueChange={setFilter} className="mb-6">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="unread">Unread</TabsTrigger>
            <TabsTrigger value="read">Read</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="reminders-tab">Reminders{kpis.overdue ? ` (${kpis.overdue} overdue)` : ''}</TabsTrigger>
          </TabsList>
        </Tabs>

        {filter === 'reminders' ? (
          <ReminderCalendar getAuthHeaders={getAuthHeaders} isAdmin={isAdmin} onCountsChange={onReminderChange} />
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Card>
            <CardContent className="py-16">
              <div className="text-center text-gray-500">
                <Bell className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No notifications</p>
                <p className="text-sm mt-2">You're all caught up!</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <Card
                key={notification.id}
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  getPriorityColor(notification.priority)
                } ${!notification.read ? 'bg-blue-50' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <span className="text-3xl">{getNotificationIcon(notification.type)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`text-sm font-semibold ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </h3>
                          {!notification.read && (
                            <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                          <span>{formatTime(notification.created_at)}</span>
                          {notification.priority !== 'normal' && (
                            <Badge variant="outline" className="text-xs">
                              {notification.priority}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      {!notification.read && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Notifications;
